import { useMemo, useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import {
    Sparkle,
    Download,
    Pencil,
    X,
    Plus,
    Target,
    ArrowsLeftRight,
    CaretRight,
    CaretDown,
    MagicWand,
    Megaphone,
} from "@phosphor-icons/react";
import { csvDownload } from "@/lib/csvRows";
import { generateRSAsForAdGroups, errText } from "@/lib/api";

// ---- Intent-based theme patterns (priority: first match wins) ----
// Carefully ordered so the most specific intent (Emergency, Price) wins
// before falling into general buckets (Service, Near Me).
const THEME_PATTERNS = [
    {
        id: "emergency",
        name: "Emergency / 24-7",
        words: [
            "emergency",
            "urgent",
            "24 hour",
            "24-hour",
            "24/7",
            "24 7",
            "same day",
            "same-day",
            "today",
            "tonight",
            "weekend",
            "overnight",
        ],
    },
    {
        id: "price",
        name: "Price / Cost",
        words: [
            "price",
            "prices",
            "pricing",
            "cost",
            "costs",
            "cheap",
            "cheapest",
            "affordable",
            "quote",
            "quotes",
            "quotation",
            "estimate",
            "estimates",
            "rate",
            "rates",
            "fee",
            "fees",
            "how much",
        ],
    },
    {
        id: "repair",
        name: "Repair / Fix",
        words: [
            "repair",
            "repairs",
            "repairing",
            "fix",
            "fixing",
            "broken",
            "replace",
            "replacement",
            "not working",
            "stopped working",
            "faulty",
            "leaking",
        ],
    },
    {
        id: "installation",
        name: "Installation / Setup",
        words: [
            "install",
            "installation",
            "installing",
            "setup",
            "set up",
            "fit",
            "fitting",
            "fitted",
            "fitter",
        ],
    },
    {
        id: "service",
        name: "Service / Maintenance",
        words: [
            "service",
            "services",
            "servicing",
            "maintain",
            "maintenance",
            "clean",
            "cleaning",
            "unblock",
            "unblocking",
            "flush",
            "flushing",
            "inspection",
            "annual",
        ],
    },
    {
        id: "nearme",
        name: "Near Me / Local",
        words: ["near me", "nearby", "local", "closest", "in my area"],
    },
    {
        id: "commercial",
        name: "Commercial / Business",
        words: [
            "commercial",
            "business",
            "office",
            "offices",
            "industrial",
            "warehouse",
            "restaurant",
            "retail",
        ],
    },
    {
        id: "residential",
        name: "Residential / Home",
        words: [
            "home",
            "residential",
            "domestic",
            "house",
            "houses",
            "property",
            "homes",
            "flat",
            "apartment",
        ],
    },
    {
        id: "comparison",
        name: "Comparison / Research",
        words: [
            "best",
            "top",
            "review",
            "reviews",
            " vs ",
            " versus ",
            "compare",
            "comparison",
            "difference between",
        ],
    },
    {
        id: "company",
        name: "Company / Provider",
        words: [
            "company",
            "companies",
            "contractor",
            "contractors",
            "specialist",
            "specialists",
            "expert",
            "experts",
            "professional",
            "professionals",
            "firm",
            "agency",
        ],
    },
];

const STOP_WORDS = new Set([
    "a", "an", "the", "for", "to", "in", "on", "at", "by", "of", "and", "or",
    "my", "me", "near", "with", "best", "top", "local", "new", "cheap", "cost",
    "price", "quote",
]);

const detectTheme = (keyword) => {
    const kw = ` ${(keyword || "").toLowerCase()} `;
    for (const t of THEME_PATTERNS) {
        for (const w of t.words) {
            // phrase match for multi-word patterns, word-boundary for single
            if (w.includes(" ")) {
                if (kw.includes(` ${w} `) || kw.includes(` ${w}`)) return t;
            } else {
                if (new RegExp(`\\b${w}\\b`, "i").test(keyword || "")) return t;
            }
        }
    }
    return null;
};

const titleCase = (s) =>
    (s || "")
        .toLowerCase()
        .split(/\s+/)
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
        .join(" ")
        .trim();

// Derive a short "base noun" for an ad-group label by stripping theme trigger
// words + stop words from all keywords in the group and picking the most
// common surviving unigram or bigram.
const deriveBaseNoun = (keywords, themeWords) => {
    const triggers = new Set(themeWords.flatMap((w) => w.split(" ")).map((w) => w.toLowerCase()));
    const counts = new Map();
    for (const k of keywords) {
        const tokens = (k || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
        const clean = tokens.filter((t) => !triggers.has(t) && !STOP_WORDS.has(t));
        for (const t of clean) counts.set(t, (counts.get(t) || 0) + 1);
        for (let i = 0; i < clean.length - 1; i++) {
            const bi = `${clean[i]} ${clean[i + 1]}`;
            counts.set(bi, (counts.get(bi) || 0) + 1);
        }
    }
    let best = "";
    let bestCount = 0;
    for (const [tok, c] of counts.entries()) {
        // Prefer bigrams on ties.
        const score = c * (tok.includes(" ") ? 1.2 : 1);
        if (score > bestCount) {
            best = tok;
            bestCount = score;
        }
    }
    return titleCase(best) || "Service";
};

const buildAdGroupName = (themeName, keywords, themeWords) => {
    const base = deriveBaseNoun(keywords, themeWords);
    if (themeName === "General") return base;
    return `${base} — ${themeName.split(" / ")[0]}`;
};

export const KeywordThemer = ({ keywords, defaultCampaignName = "Keyword Research Plan", defaultMatchType = "Phrase" }) => {
    // keywords: Array<{ keyword, _source?, avg_monthly_searches?, competition?, ... }>
    const [themes, setThemes] = useState(null); // null until Auto-Theme is clicked
    const [adGroups, setAdGroups] = useState(null); // generated structure
    const [editingThemeId, setEditingThemeId] = useState(null);
    const [editingName, setEditingName] = useState("");
    const [collapsed, setCollapsed] = useState({});
    const [campaignName, setCampaignName] = useState(defaultCampaignName);
    const [matchType, setMatchType] = useState(defaultMatchType); // Broad|Phrase|Exact

    // Ads generation state
    const [generatingAds, setGeneratingAds] = useState(false);
    const [generatedAds, setGeneratedAds] = useState(null); // Array<{ad_group_name, theme_name, headlines[], descriptions[], path1, path2}>
    const [businessSummary, setBusinessSummary] = useState("");
    const [uspsText, setUspsText] = useState(""); // one per line
    const [finalUrl, setFinalUrl] = useState("");
    const [showAdsPanel, setShowAdsPanel] = useState(false);

    // Generated ads live only in this tab's memory. Warn before a refresh or
    // tab close throws them away so the user can export the CSV first.
    useEffect(() => {
        if (!generatedAds?.length) return undefined;
        const warn = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [generatedAds]);

    const autoTheme = useCallback(() => {
        if (!keywords?.length) {
            toast.error("No keywords to theme — adjust filters or run a fetch");
            return;
        }
        const buckets = new Map();
        for (const t of THEME_PATTERNS) buckets.set(t.id, { ...t, keywords: [] });
        buckets.set("general", {
            id: "general",
            name: "General",
            words: [],
            keywords: [],
        });
        for (const row of keywords) {
            const t = detectTheme(row.keyword);
            const bucket = buckets.get(t?.id || "general");
            bucket.keywords.push(row);
        }
        const nonEmpty = Array.from(buckets.values()).filter(
            (b) => b.keywords.length > 0
        );
        setThemes(nonEmpty);
        setAdGroups(null);
        toast.success(
            `Grouped ${keywords.length} keyword${keywords.length === 1 ? "" : "s"} into ${nonEmpty.length} theme${nonEmpty.length === 1 ? "" : "s"}`
        );
    }, [keywords]);

    const resetThemes = useCallback(() => {
        setThemes(null);
        setAdGroups(null);
        setEditingThemeId(null);
    }, []);

    const renameTheme = (id, name) => {
        setThemes((prev) =>
            prev.map((t) => (t.id === id ? { ...t, name: name.trim() || t.name } : t))
        );
        setAdGroups(null); // ad group names should regenerate after rename
    };

    const moveKeyword = (keyword, fromId, toId) => {
        if (fromId === toId) return;
        setThemes((prev) => {
            let moved = null;
            const next = prev.map((t) => {
                if (t.id === fromId) {
                    const kept = [];
                    for (const k of t.keywords) {
                        if (k.keyword === keyword && !moved) moved = k;
                        else kept.push(k);
                    }
                    return { ...t, keywords: kept };
                }
                return t;
            });
            if (!moved) return prev;
            if (toId === "__new__") {
                const newId = `custom_${Date.now()}`;
                return [
                    ...next,
                    { id: newId, name: "New Theme", words: [], keywords: [moved] },
                ];
            }
            return next.map((t) =>
                t.id === toId ? { ...t, keywords: [...t.keywords, moved] } : t
            );
        });
        setAdGroups(null);
    };

    const generateAdGroups = useCallback(() => {
        if (!themes?.length) {
            toast.error("Run Auto-Theme first");
            return;
        }
        const groups = themes
            .filter((t) => t.keywords.length > 0)
            .map((t) => ({
                theme_id: t.id,
                theme_name: t.name,
                ad_group_name: buildAdGroupName(t.name, t.keywords.map((k) => k.keyword), t.words),
                keywords: t.keywords,
            }));
        setAdGroups(groups);
        toast.success(`Generated ${groups.length} ad group${groups.length === 1 ? "" : "s"}`);
    }, [themes]);

    const renameAdGroup = (theme_id, newName) => {
        setAdGroups((prev) =>
            prev.map((g) =>
                g.theme_id === theme_id
                    ? { ...g, ad_group_name: newName.trim() || g.ad_group_name }
                    : g
            )
        );
    };

    const exportAdGroups = useCallback(() => {
        const source = adGroups ||
            (themes || [])
                .filter((t) => t.keywords.length > 0)
                .map((t) => ({
                    theme_id: t.id,
                    theme_name: t.name,
                    ad_group_name: buildAdGroupName(t.name, t.keywords.map((k) => k.keyword), t.words),
                    keywords: t.keywords,
                }));
        if (!source.length) {
            toast.error("Generate ad groups first");
            return;
        }
        const rows = [];
        for (const g of source) {
            for (const k of g.keywords) {
                rows.push({
                    Campaign: campaignName,
                    "Ad group": g.ad_group_name,
                    Keyword: k.keyword,
                    "Criterion Type": matchType,
                    Status: "Paused",
                    "Max CPC": "",
                    "Avg monthly searches": k.avg_monthly_searches || "",
                    Competition: k.competition || "",
                    Theme: g.theme_name,
                    Source: k._source || "Planner",
                });
            }
        }
        if (!rows.length) {
            toast.error("No keywords in any theme");
            return;
        }
        csvDownload("keyword-research-ad-groups.csv", rows);
        toast.success(
            `Exported ${rows.length} keyword${rows.length === 1 ? "" : "s"} across ${source.length} ad group${source.length === 1 ? "" : "s"}`
        );
    }, [adGroups, themes, campaignName, matchType]);

    // ---- RSA generation (one Claude call per ad group, up to 4 in parallel) ----
    const effectiveAdGroups = useCallback(() => {
        return (
            adGroups ||
            (themes || [])
                .filter((t) => t.keywords.length > 0)
                .map((t) => ({
                    theme_id: t.id,
                    theme_name: t.name,
                    ad_group_name: buildAdGroupName(
                        t.name,
                        t.keywords.map((k) => k.keyword),
                        t.words
                    ),
                    keywords: t.keywords,
                }))
        );
    }, [adGroups, themes]);

    const generateAds = useCallback(async () => {
        const source = effectiveAdGroups();
        if (!source.length) {
            toast.error("Run Auto-Theme first");
            return;
        }
        setGeneratingAds(true);
        setShowAdsPanel(true);
        // Scroll the new Generated Ads panel into view so it's not lost
        // below the fold (fixes the "I generated ads but can't see them" bug).
        setTimeout(() => {
            document
                .querySelector('[data-testid="kt-ads-panel"]')
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
        const t = toast.loading(`Generating ads for ${source.length} ad group${source.length === 1 ? "" : "s"}…`);
        try {
            const res = await generateRSAsForAdGroups({
                ad_groups: source.map((g) => ({
                    ad_group_name: g.ad_group_name,
                    theme_name: g.theme_name,
                    keywords: g.keywords.map((k) => k.keyword),
                })),
                business_summary: businessSummary,
                usps: uspsText
                    .split(/\n+/)
                    .map((s) => s.trim())
                    .filter(Boolean),
                final_url: finalUrl,
            });
            toast.dismiss(t);
            setGeneratedAds(res.ads || []);
            const okCount = (res.ads || []).filter((a) => (a.headlines || []).length > 0).length;
            if (okCount === source.length) {
                toast.success(`Generated ${okCount} ad${okCount === 1 ? "" : "s"}`);
            } else {
                toast.warning(
                    `Generated ${okCount}/${source.length} ads — some ad groups failed. See errors inline.`
                );
            }
        } catch (e) {
            toast.dismiss(t);
            toast.error(errText(e, "Could not generate ads"));
        } finally {
            setGeneratingAds(false);
        }
    }, [effectiveAdGroups, businessSummary, uspsText, finalUrl]);

    const updateAdField = (ad_group_name, field, value) => {
        setGeneratedAds((prev) =>
            (prev || []).map((a) =>
                a.ad_group_name === ad_group_name ? { ...a, [field]: value } : a
            )
        );
    };

    const updateAdLine = (ad_group_name, kind, idx, value) => {
        const limit = kind === "headlines" ? 30 : 90;
        const clipped = (value || "").slice(0, limit);
        setGeneratedAds((prev) =>
            (prev || []).map((a) => {
                if (a.ad_group_name !== ad_group_name) return a;
                const list = [...(a[kind] || [])];
                list[idx] = clipped;
                return { ...a, [kind]: list };
            })
        );
    };

    const exportAdsCsv = useCallback(() => {
        const ads = (generatedAds || []).filter((a) => (a.headlines || []).length > 0);
        if (!ads.length) {
            toast.error("No ads to export — generate first");
            return;
        }
        // Phase-1 (c): hard char-limit validator — fail loudly if any field is over.
        const violations = [];
        for (const a of ads) {
            (a.headlines || []).forEach((h, i) => {
                if ((h || "").length > 30)
                    violations.push(`${a.ad_group_name} · Headline ${i + 1}: ${h.length}/30`);
            });
            (a.descriptions || []).forEach((d, i) => {
                if ((d || "").length > 90)
                    violations.push(`${a.ad_group_name} · Description ${i + 1}: ${d.length}/90`);
            });
            if ((a.path1 || "").length > 15)
                violations.push(`${a.ad_group_name} · Path 1: ${a.path1.length}/15`);
            if ((a.path2 || "").length > 15)
                violations.push(`${a.ad_group_name} · Path 2: ${a.path2.length}/15`);
        }
        if (violations.length) {
            toast.error(
                `${violations.length} field${violations.length === 1 ? "" : "s"} over Google limits — fix before export`,
                { description: violations.slice(0, 4).join(" · ") }
            );
            return;
        }
        // Google Ads Editor "Ads" CSV import format for Responsive Search Ads.
        const rows = ads.map((a) => {
            const row = {
                Campaign: campaignName,
                "Ad group": a.ad_group_name,
                "Ad type": "Responsive search ad",
                Status: "Paused",
            };
            for (let i = 0; i < 15; i++) {
                row[`Headline ${i + 1}`] = a.headlines[i] || "";
                row[`Headline ${i + 1} position`] = "";
            }
            for (let i = 0; i < 4; i++) {
                row[`Description ${i + 1}`] = a.descriptions[i] || "";
                row[`Description ${i + 1} position`] = "";
            }
            row["Final URL"] = finalUrl || "";
            row["Path 1"] = a.path1 || "";
            row["Path 2"] = a.path2 || "";
            row["Theme"] = a.theme_name || "";
            return row;
        });
        csvDownload("keyword-research-ads.csv", rows);
        toast.success(`Exported ${ads.length} RSA${ads.length === 1 ? "" : "s"}`);
    }, [generatedAds, campaignName, finalUrl]);

    const totalInThemes = useMemo(
        () => (themes || []).reduce((n, t) => n + t.keywords.length, 0),
        [themes]
    );

    if (!keywords?.length) {
        return (
            <div
                data-testid="kt-empty"
                className="border border-dashed border-neutral-300 bg-white px-4 py-6 text-center text-neutral-500 font-mono text-xs uppercase tracking-widest"
            >
                No keywords in the current filtered view
            </div>
        );
    }

    return (
        <div data-testid="kt-root" className="border border-ink bg-white">
            {/* Toolbar */}
            <div className="bg-ink text-white px-4 py-2 flex items-center flex-wrap gap-2">
                <Target size={14} weight="bold" />
                <h3 className="font-heading font-black text-sm uppercase tracking-widest">
                    Themes & Ad Groups
                </h3>
                <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-white/60">
                    {keywords.length} keyword{keywords.length === 1 ? "" : "s"} in scope
                </span>
                <div className="ml-auto flex items-center gap-2 flex-wrap">
                    {!themes ? (
                        <button
                            data-testid="kt-auto-theme"
                            onClick={autoTheme}
                            className="brutal-btn h-7 px-2 border border-white bg-signal text-ink hover:bg-signal/90 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                        >
                            <Sparkle size={11} weight="bold" /> Auto-Theme
                        </button>
                    ) : (
                        <>
                            <button
                                data-testid="kt-generate-adgroups"
                                onClick={generateAdGroups}
                                className="brutal-btn h-7 px-2 border border-white bg-signal text-ink hover:bg-signal/90 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                            >
                                <Target size={11} weight="bold" /> Generate Ad Groups
                            </button>
                            <button
                                data-testid="kt-generate-ads"
                                onClick={generateAds}
                                disabled={generatingAds}
                                className="brutal-btn h-7 px-2 border border-white bg-klein text-white hover:bg-klein/90 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-50"
                                title="Use AI to generate a Responsive Search Ad for each ad group"
                            >
                                <MagicWand size={11} weight="bold" />
                                {generatingAds ? "Generating…" : "Generate Ads"}
                            </button>
                            {generatedAds && generatedAds.length > 0 && (
                                <button
                                    data-testid="kt-jump-to-ads"
                                    onClick={() =>
                                        document
                                            .querySelector('[data-testid="kt-ads-panel"]')
                                            ?.scrollIntoView({ behavior: "smooth", block: "start" })
                                    }
                                    className="brutal-btn h-7 px-2 border border-white bg-signal/20 text-white hover:bg-signal/30 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                                    title="Jump to the Generated Ads panel"
                                >
                                    ↓ {generatedAds.filter((a) => (a.headlines || []).length > 0).length} Ads
                                </button>
                            )}
                            <button
                                data-testid="kt-export-adgroups"
                                onClick={exportAdGroups}
                                className="brutal-btn h-7 px-2 border border-white bg-white text-ink hover:bg-neutral-100 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                            >
                                <Download size={11} weight="bold" /> Export Ad Groups
                            </button>
                            <button
                                data-testid="kt-reset"
                                onClick={resetThemes}
                                className="brutal-btn h-7 px-2 border border-white/40 bg-transparent text-white/80 hover:bg-white/10 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                            >
                                <X size={11} weight="bold" /> Reset
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Pre-theme state */}
            {!themes && (
                <div className="px-4 py-6 text-center text-neutral-600 font-mono text-xs">
                    <Sparkle size={20} weight="duotone" className="mx-auto mb-2 text-klein" />
                    Group these keywords into intent-based themes (Repair,
                    Installation, Emergency, Price…) with one click. Then generate
                    ad group names ready to upload to Google Ads.
                </div>
            )}

            {/* Themes */}
            {themes && (
                <>
                    {/* Export settings */}
                    <div className="bg-neutral-50 border-b border-neutral-300 px-4 py-2 flex items-center flex-wrap gap-3 text-[11px] font-mono">
                        <span className="uppercase tracking-widest text-[10px] font-bold text-neutral-500">
                            Campaign:
                        </span>
                        <input
                            data-testid="kt-campaign-name"
                            value={campaignName}
                            onChange={(e) => setCampaignName(e.target.value)}
                            className="h-6 w-56 border border-neutral-300 focus:border-ink focus:outline-none px-2 text-[11px] font-mono"
                        />
                        <span className="mx-2 h-4 w-px bg-neutral-300" />
                        <span className="uppercase tracking-widest text-[10px] font-bold text-neutral-500">
                            Match type:
                        </span>
                        {["Broad", "Phrase", "Exact"].map((m) => (
                            <button
                                key={m}
                                data-testid={`kt-match-${m.toLowerCase()}`}
                                onClick={() => setMatchType(m)}
                                className={`h-6 px-2 border font-mono uppercase tracking-widest text-[10px] font-bold ${
                                    matchType === m
                                        ? "border-ink bg-ink text-white"
                                        : "border-neutral-300 bg-white text-neutral-500 hover:text-ink"
                                }`}
                            >
                                {m}
                            </button>
                        ))}
                        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                            {totalInThemes} in {themes.length} theme{themes.length === 1 ? "" : "s"}
                        </span>
                    </div>

                    <div className="divide-y divide-neutral-200">
                        {themes.map((t) => {
                            const isCollapsed = !!collapsed[t.id];
                            const ag = (adGroups || []).find((g) => g.theme_id === t.id);
                            return (
                                <section key={t.id} data-testid={`kt-theme-${t.id}`} className="bg-white">
                                    <header className="px-4 py-2 flex items-center gap-2 bg-neutral-50 border-b border-neutral-200">
                                        <button
                                            data-testid={`kt-collapse-${t.id}`}
                                            onClick={() =>
                                                setCollapsed((prev) => ({
                                                    ...prev,
                                                    [t.id]: !prev[t.id],
                                                }))
                                            }
                                            className="text-neutral-500 hover:text-ink"
                                        >
                                            {isCollapsed ? (
                                                <CaretRight size={14} weight="bold" />
                                            ) : (
                                                <CaretDown size={14} weight="bold" />
                                            )}
                                        </button>
                                        {editingThemeId === t.id ? (
                                            <input
                                                data-testid={`kt-rename-input-${t.id}`}
                                                value={editingName}
                                                autoFocus
                                                onChange={(e) => setEditingName(e.target.value)}
                                                onBlur={() => {
                                                    renameTheme(t.id, editingName);
                                                    setEditingThemeId(null);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        renameTheme(t.id, editingName);
                                                        setEditingThemeId(null);
                                                    } else if (e.key === "Escape") {
                                                        setEditingThemeId(null);
                                                    }
                                                }}
                                                className="h-6 border border-ink px-2 text-[12px] font-heading font-black"
                                            />
                                        ) : (
                                            <h4 className="font-heading font-black text-[13px] uppercase tracking-widest flex items-center gap-2">
                                                {t.name}
                                                <button
                                                    data-testid={`kt-rename-${t.id}`}
                                                    onClick={() => {
                                                        setEditingThemeId(t.id);
                                                        setEditingName(t.name);
                                                    }}
                                                    className="text-neutral-400 hover:text-ink"
                                                    title="Rename theme"
                                                >
                                                    <Pencil size={11} weight="bold" />
                                                </button>
                                            </h4>
                                        )}
                                        <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                                            {t.keywords.length} keyword{t.keywords.length === 1 ? "" : "s"}
                                        </span>
                                        {ag && (
                                            <span className="ml-auto flex items-center gap-2">
                                                <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                                                    Ad group:
                                                </span>
                                                <input
                                                    data-testid={`kt-adgroup-name-${t.id}`}
                                                    value={ag.ad_group_name}
                                                    onChange={(e) =>
                                                        renameAdGroup(t.id, e.target.value)
                                                    }
                                                    className="h-6 w-64 border border-neutral-300 focus:border-ink focus:outline-none px-2 text-[11px] font-mono"
                                                />
                                            </span>
                                        )}
                                    </header>
                                    {!isCollapsed && (
                                        <ul className="divide-y divide-neutral-100">
                                            {t.keywords.map((k) => (
                                                <li
                                                    key={`${t.id}:${k.keyword}`}
                                                    data-testid={`kt-row-${k.keyword}`}
                                                    className="px-4 py-1.5 flex items-center gap-3 text-[12px]"
                                                >
                                                    <span className="flex-1 font-medium text-ink">
                                                        {k.keyword}
                                                    </span>
                                                    {typeof k.avg_monthly_searches ===
                                                        "number" && (
                                                        <span className="text-[10px] font-mono text-neutral-500 w-16 text-right">
                                                            {k.avg_monthly_searches.toLocaleString()}
                                                        </span>
                                                    )}
                                                    <span className="text-[9px] font-mono uppercase tracking-widest text-neutral-400 w-20 text-right">
                                                        {k.competition || ""}
                                                    </span>
                                                    <label className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-neutral-500">
                                                        <ArrowsLeftRight size={10} weight="bold" />
                                                        <select
                                                            data-testid={`kt-move-${k.keyword}`}
                                                            value={t.id}
                                                            onChange={(e) =>
                                                                moveKeyword(
                                                                    k.keyword,
                                                                    t.id,
                                                                    e.target.value
                                                                )
                                                            }
                                                            className="h-6 border border-neutral-300 bg-white px-1 text-[10px] font-mono"
                                                        >
                                                            {themes.map((opt) => (
                                                                <option
                                                                    key={opt.id}
                                                                    value={opt.id}
                                                                >
                                                                    {opt.name}
                                                                </option>
                                                            ))}
                                                            <option value="__new__">
                                                                + New Theme…
                                                            </option>
                                                        </select>
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </section>
                            );
                        })}
                    </div>

                    {/* Generated Ads panel */}
                    {(showAdsPanel || generatedAds) && (
                        <AdsPanel
                            ads={generatedAds}
                            loading={generatingAds}
                            businessSummary={businessSummary}
                            setBusinessSummary={setBusinessSummary}
                            uspsText={uspsText}
                            setUspsText={setUspsText}
                            finalUrl={finalUrl}
                            setFinalUrl={setFinalUrl}
                            updateAdLine={updateAdLine}
                            updateAdField={updateAdField}
                            onExport={exportAdsCsv}
                            onRegenerate={generateAds}
                            onClose={() => {
                                if (
                                    generatedAds?.length &&
                                    !window.confirm("Close and discard the generated ads? Export the CSV first if you need them.")
                                ) {
                                    return;
                                }
                                setShowAdsPanel(false);
                                setGeneratedAds(null);
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
};

const AdsPanel = ({
    ads,
    loading,
    businessSummary,
    setBusinessSummary,
    uspsText,
    setUspsText,
    finalUrl,
    setFinalUrl,
    updateAdLine,
    updateAdField,
    onExport,
    onRegenerate,
    onClose,
}) => {
    return (
        <div
            data-testid="kt-ads-panel"
            className="border-t-2 border-ink bg-neutral-50"
        >
            <header className="bg-klein text-white px-4 py-2 flex items-center flex-wrap gap-2">
                <Megaphone size={14} weight="bold" />
                <h3 className="font-heading font-black text-sm uppercase tracking-widest">
                    Generated Ads
                </h3>
                <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-white/70">
                    {ads ? `${ads.filter((a) => (a.headlines || []).length > 0).length} / ${ads.length} ad${ads.length === 1 ? "" : "s"}` : loading ? "Generating…" : ""}
                </span>
                <div className="ml-auto flex items-center gap-2">
                    <button
                        data-testid="kt-regen-ads"
                        onClick={onRegenerate}
                        disabled={loading}
                        className="brutal-btn h-7 px-2 border border-white bg-transparent text-white hover:bg-white/10 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
                    >
                        <MagicWand size={11} weight="bold" /> Regenerate
                    </button>
                    <button
                        data-testid="kt-export-ads"
                        onClick={onExport}
                        disabled={loading || !ads?.length}
                        className="brutal-btn h-7 px-2 border border-white bg-white text-ink hover:bg-neutral-100 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
                    >
                        <Download size={11} weight="bold" /> Export Ads CSV
                    </button>
                    <button
                        data-testid="kt-close-ads"
                        onClick={onClose}
                        className="brutal-btn h-7 w-7 border border-white/40 bg-transparent text-white/80 hover:bg-white/10 flex items-center justify-center"
                        title="Close ads panel"
                    >
                        <X size={11} weight="bold" />
                    </button>
                </div>
            </header>

            {/* Settings row (business summary + USPs + final URL) */}
            <div className="bg-white border-b border-neutral-200 px-4 py-2 flex flex-col gap-2 text-[11px] font-mono">
                <div className="flex items-center flex-wrap gap-3">
                    <span className="uppercase tracking-widest text-[10px] font-bold text-neutral-500">
                        Business:
                    </span>
                    <input
                        data-testid="kt-business-summary"
                        value={businessSummary}
                        onChange={(e) => setBusinessSummary(e.target.value)}
                        placeholder="e.g. Bengaluru sump cleaning for homes & commercial sites"
                        className="h-6 flex-1 min-w-[220px] border border-neutral-300 focus:border-ink focus:outline-none px-2 text-[11px] font-mono"
                    />
                    <span className="uppercase tracking-widest text-[10px] font-bold text-neutral-500">
                        Final URL:
                    </span>
                    <input
                        data-testid="kt-final-url"
                        value={finalUrl}
                        onChange={(e) => setFinalUrl(e.target.value)}
                        placeholder="https://your-landing.com/sump-cleaning"
                        className="h-6 w-72 border border-neutral-300 focus:border-ink focus:outline-none px-2 text-[11px] font-mono"
                    />
                </div>
                <div className="flex items-start gap-3">
                    <span className="uppercase tracking-widest text-[10px] font-bold text-neutral-500 pt-1">
                        USPs <span className="text-red-600">*</span>:
                    </span>
                    <textarea
                        data-testid="kt-usps"
                        value={uspsText}
                        onChange={(e) => setUspsText(e.target.value)}
                        rows={3}
                        placeholder={"One per line — Claude will build >=3 headlines from these.\ne.g.\n100% satisfaction guarantee\nService in 1 hour\nCertified cleaning chemicals\n6 years in Bengaluru"}
                        className="flex-1 min-h-[64px] border border-neutral-300 focus:border-ink focus:outline-none px-2 py-1 text-[11px] font-mono resize-y"
                    />
                </div>
            </div>

            {loading && !ads && (
                <div className="px-4 py-8 text-center text-neutral-500 font-mono text-xs uppercase tracking-widest">
                    Writing responsive search ads with Claude Sonnet…
                </div>
            )}

            {ads && (
                <div className="divide-y divide-neutral-200">
                    {ads.map((ad) => {
                        const hasError = !ad.headlines?.length && ad.error;
                        return (
                            <section
                                key={ad.ad_group_name}
                                data-testid={`kt-ad-${ad.ad_group_name}`}
                                className="bg-white"
                            >
                                <header className="px-4 py-2 flex items-center flex-wrap gap-3 bg-neutral-50 border-b border-neutral-200">
                                    <h4 className="font-heading font-black text-[12px] uppercase tracking-widest">
                                        {ad.ad_group_name}
                                    </h4>
                                    {ad.theme_name && (
                                        <span className="text-[9px] font-mono uppercase tracking-widest bg-klein/10 text-klein border border-klein/40 px-1.5 py-0.5">
                                            {ad.theme_name}
                                        </span>
                                    )}
                                    {hasError && (
                                        <span className="text-[10px] font-mono text-red-700">
                                            {ad.error}
                                        </span>
                                    )}
                                    <span className="ml-auto flex items-center gap-2 text-[10px] font-mono text-neutral-500">
                                        <span>Path:</span>
                                        <span className="text-neutral-700">/</span>
                                        <input
                                            data-testid={`kt-path1-${ad.ad_group_name}`}
                                            value={ad.path1 || ""}
                                            onChange={(e) =>
                                                updateAdField(
                                                    ad.ad_group_name,
                                                    "path1",
                                                    e.target.value.slice(0, 15)
                                                )
                                            }
                                            maxLength={15}
                                            placeholder="path1"
                                            className="h-6 w-24 border border-neutral-300 focus:border-ink focus:outline-none px-1 text-[11px] font-mono"
                                        />
                                        <span className="text-neutral-700">/</span>
                                        <input
                                            data-testid={`kt-path2-${ad.ad_group_name}`}
                                            value={ad.path2 || ""}
                                            onChange={(e) =>
                                                updateAdField(
                                                    ad.ad_group_name,
                                                    "path2",
                                                    e.target.value.slice(0, 15)
                                                )
                                            }
                                            maxLength={15}
                                            placeholder="path2"
                                            className="h-6 w-24 border border-neutral-300 focus:border-ink focus:outline-none px-1 text-[11px] font-mono"
                                        />
                                    </span>
                                </header>
                                {!hasError && (
                                    <div className="px-4 py-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div>
                                            <div className="font-mono uppercase tracking-widest text-[10px] font-bold text-neutral-500 mb-1">
                                                Headlines ({(ad.headlines || []).length}/15 · ≤30 chars)
                                            </div>
                                            <ul className="space-y-1">
                                                {(ad.headlines || []).map((h, i) => (
                                                    <li
                                                        key={i}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <span className="w-5 text-right text-[10px] font-mono text-neutral-400">
                                                            {i + 1}
                                                        </span>
                                                        <input
                                                            data-testid={`kt-headline-${ad.ad_group_name}-${i}`}
                                                            value={h}
                                                            maxLength={30}
                                                            onChange={(e) =>
                                                                updateAdLine(
                                                                    ad.ad_group_name,
                                                                    "headlines",
                                                                    i,
                                                                    e.target.value
                                                                )
                                                            }
                                                            className="h-7 flex-1 border border-neutral-300 focus:border-ink focus:outline-none px-2 text-[12px] font-mono"
                                                        />
                                                        <span
                                                            className={`w-10 text-right text-[10px] font-mono ${
                                                                h.length > 30
                                                                    ? "text-red-600"
                                                                    : "text-neutral-400"
                                                            }`}
                                                        >
                                                            {h.length}/30
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <div className="font-mono uppercase tracking-widest text-[10px] font-bold text-neutral-500 mb-1">
                                                Descriptions ({(ad.descriptions || []).length}/4 · ≤90 chars)
                                            </div>
                                            <ul className="space-y-1">
                                                {(ad.descriptions || []).map((d, i) => (
                                                    <li
                                                        key={i}
                                                        className="flex items-start gap-2"
                                                    >
                                                        <span className="w-5 pt-1 text-right text-[10px] font-mono text-neutral-400">
                                                            {i + 1}
                                                        </span>
                                                        <textarea
                                                            data-testid={`kt-description-${ad.ad_group_name}-${i}`}
                                                            value={d}
                                                            maxLength={90}
                                                            rows={2}
                                                            onChange={(e) =>
                                                                updateAdLine(
                                                                    ad.ad_group_name,
                                                                    "descriptions",
                                                                    i,
                                                                    e.target.value
                                                                )
                                                            }
                                                            className="min-h-[32px] flex-1 border border-neutral-300 focus:border-ink focus:outline-none px-2 py-1 text-[12px] font-mono resize-y"
                                                        />
                                                        <span
                                                            className={`w-10 pt-1 text-right text-[10px] font-mono ${
                                                                d.length > 90
                                                                    ? "text-red-600"
                                                                    : "text-neutral-400"
                                                            }`}
                                                        >
                                                            {d.length}/90
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default KeywordThemer;
